import React, { useEffect, useState } from "react";
import api from "../api";
import { Eye, EyeOff } from "lucide-react"; // Iconos para mostrar/ocultar contraseñas



const EditUser = () => {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); 
  const [success, setSuccess] = useState(null); 

  // Estado de visibilidad de contraseñas
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const [formData, setFormData] = useState({
    correo: "",
    telefono: "",
    contrasena_actual: "",
    nueva_contrasena: "",
    confirmar_contrasena: "",
  });

  const token = localStorage.getItem("token") || null;
  // 🔹 Formateadores visuales
  const formatTelefono = (tel) => {
    if (!tel) return "";
    const clean = tel.replace(/\D/g, "");
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
    return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6, 10)}`;
  };

  const formatSalario = (sal) => {
    if (!sal) return "";
    const clean = sal.toString().replace(/\D/g, "");
    return `$ ${Number(clean).toLocaleString("es-CO")}`;
  };

  // 🔹 Obtener datos del usuario autenticado
  useEffect(() => {
    const fetchUser = async () => {
      try {
        console.debug('[EditUser] token:', token, 'storedUser:', JSON.parse(localStorage.getItem('user') || 'null'));
        const response = await api.get(`/usuario-logeado`);
        const usuarioData = response.data.usuario;
        setUsuario(usuarioData);

        // Obtener el último período de contrato
        const ultimoContrato = usuarioData.empleado?.periodosContrato && usuarioData.empleado.periodosContrato.length > 0
          ? usuarioData.empleado.periodosContrato[usuarioData.empleado.periodosContrato.length - 1]
          : null;

        // Inicializar formulario editable
        setFormData({
          correo: usuarioData.correo || "",
          telefono: usuarioData.telefono || "",
          contrasena_actual: "",
          nueva_contrasena: "",
          confirmar_contrasena: "",
        });

        // Almacenar el último contrato en el estado para mostrarlo
        if (ultimoContrato) {
          setUsuario(prev => ({
            ...prev,
            cargo: ultimoContrato.cargo.nombre,
            tipo_contrato: ultimoContrato.tipo_contrato.nombre,
            salario: ultimoContrato.salario,
            fecha_ingreso: ultimoContrato.fecha_inicio,
            fecha_fin_contrato: ultimoContrato.fecha_fin,
          }));
        }

      } catch (err) {
        console.error("❌ Error al obtener usuario logeado:", err);
        setError("No se pudo cargar la información del usuario.");
      } finally {
        setLoading(false);
      }
    };

    if (!token) {
      setError('No autorizado. Por favor inicia sesión.');
      setLoading(false);
      return;
    }
    fetchUser();
  }, [token]);

  // 🔹 Manejo de cambios en los inputs editables
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🔹 Enviar actualización al backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const storedUser = JSON.parse(localStorage.getItem("user"));
    const userId = storedUser?.id;

    if (!userId) {
      setError("No se encontró el ID del usuario autenticado.");
      return;
    }

    if (
      formData.nueva_contrasena &&
      formData.nueva_contrasena !== formData.confirmar_contrasena
    ) {
      setError("La nueva contraseña y la confirmación no coinciden.");
      return;
    }

    try {
      const updateData = {
        correo: formData.correo,
        telefono: formData.telefono,
        contrasena_actual: formData.contrasena_actual,
        nueva_contrasena: formData.nueva_contrasena || undefined,
        nueva_contrasena_confirmation: formData.confirmar_contrasena,
      };
      console.log("Sending user update data:", updateData);

      const response = await api.post(
        `/usuarios/${userId}/actualizar`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("Server response:", response.data);
      setSuccess(response.data.mensaje || "✅ Datos actualizados correctamente");

      // Actualizar datos del usuario en localStorage
      const updatedUser = {
        ...storedUser,
        correo: formData.correo,
        telefono: formData.telefono,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUsuario(updatedUser);

      // Limpiar contraseñas del formulario
      setFormData((prev) => ({
        ...prev,
        contrasena_actual: "",
        nueva_contrasena: "",
        confirmar_contrasena: "",
      }));
    } catch (err) {
      console.error("❌ Error en la actualización:", err.response?.data || err);
      if (err.response?.data?.detalles) {
        const errorMessages = Object.values(err.response.data.detalles).flat().join(" ");
        setError(errorMessages);
      } else {
        setError(err.response?.data?.error || "Error al actualizar los datos.");
      }
    }
  };

  if (loading) return <p className="text-gray-500">Cargando datos del usuario...</p>;
  if (!usuario) return null;

  return (
    <div className="p-6 max-w-3xl mx-auto bg-white shadow-lg rounded-2xl">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">
        Editar Perfil
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 🔹 Nombre y Apellido */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600">Nombre</label>
            <input
              type="text"
              value={usuario.nombre || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Apellido</label>
            <input
              type="text"
              value={usuario.apellido || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
        </div>

        {/* 🔹 Correo y Teléfono */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600">
              Correo Electrónico
            </label>
            <input
              type="email"
              name="correo"
              value={formData.correo}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Teléfono</label>
            <input
              type="text"
              name="telefono"
              value={formatTelefono(formData.telefono)}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>

        {/* 🔹 Campos informativos adicionales (solo lectura) */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600">Fecha de Nacimiento</label>
            <input
              type="date"
              value={usuario.fecha_nacimiento || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Cargo</label>
            <input
              type="text"
              value={usuario.cargo || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Tipo de Contrato</label>
            <input
              type="text"
              value={usuario.tipo_contrato || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Salario</label>
            <input
              type="text"
              value={formatSalario(usuario.salario)}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Fecha de Inicio Contrato</label>
            <input
              type="date"
              value={usuario.fecha_ingreso || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Fecha de Fin Contrato</label>
            <input
              type="date"
              value={usuario.fecha_fin_contrato || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
        </div>

        {/* 🔹 Contraseñas */}
        <div className="relative">
          <label className="block text-sm text-gray-600">Contraseña Actual</label>
          <input
            type={passwordVisible ? "text" : "password"}
            name="contrasena_actual"
            value={formData.contrasena_actual}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
          <button
            type="button"
            onClick={() => setPasswordVisible(!passwordVisible)}
            className="absolute right-3 top-8 text-gray-500"
          >
            {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="relative">
          <label className="block text-sm text-gray-600">Nueva Contraseña</label>
          <input
            type={newPasswordVisible ? "text" : "password"}
            name="nueva_contrasena"
            value={formData.nueva_contrasena}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
          <button
            type="button"
            onClick={() => setNewPasswordVisible(!newPasswordVisible)}
            className="absolute right-3 top-8 text-gray-500"
          >
            {newPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="relative">
          <label className="block text-sm text-gray-600">
            Confirmar Nueva Contraseña
          </label>
          <input
            type={confirmPasswordVisible ? "text" : "password"}
            name="confirmar_contrasena"
            value={formData.confirmar_contrasena}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
          <button
            type="button"
            onClick={() =>
              setConfirmPasswordVisible(!confirmPasswordVisible)
            }
            className="absolute right-3 top-8 text-gray-500"
          >
            {confirmPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* 🔹 Cargo y Departamento */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600">Cargo</label>
            <input
              type="text"
              value={usuario.cargo || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Departamento</label>
            <input
              type="text"
              value={usuario.departamento || ""}
              disabled
              className="w-full p-2 border rounded bg-gray-100"
            />
          </div>
        </div>

        {/* 🔹 Mensajes dinámicos */}
        {(error || success) && (
          <div
            className={`mt-2 text-sm font-medium text-center p-2 rounded ${
              error
                ? "bg-red-100 text-red-700 border border-red-300"
                : "bg-green-100 text-green-700 border border-green-300"
            }`}
          >
            {error || success}
          </div>
        )}

        {/* 🔹 Botón principal */}
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
        >
          Guardar Cambios
        </button>
      </form>
    </div>
  );
};

export default EditUser;
